// src/pages/LicenseInvalidPage.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import appConfig from '../config/appConfig';

const LicenseInvalidPage = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    const mailtoHref =
        `mailto:${appConfig.vendorEmail}` +
        `?subject=License%20Renewal%20%E2%80%94%20${encodeURIComponent(appConfig.vendorName)}`;

    return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4 relative overflow-hidden">
            <div
                className="absolute inset-0 opacity-[0.025] pointer-events-none"
                style={{
                    backgroundImage:
                        'linear-gradient(45deg,#ffffff 25%,transparent 25%),' +
                        'linear-gradient(-45deg,#ffffff 25%,transparent 25%),' +
                        'linear-gradient(45deg,transparent 75%,#ffffff 75%),' +
                        'linear-gradient(-45deg,transparent 75%,#ffffff 75%)',
                    backgroundSize: '56px 56px',
                    backgroundPosition: '0 0, 0 28px, 28px -28px, -28px 0px',
                }}
            />

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-red-900/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-3xl pointer-events-none" />

            <div
                className={`relative z-10 w-full max-w-md transition-all duration-700 ease-out
          ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
                <div className="flex justify-center mb-6">
                    <div className="relative">
                        <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center shadow-lg shadow-red-900/20">
                            <svg
                                className="w-11 h-11 text-red-400"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M11 2h2v3h3v2h-3v3h2l2 3H7l2-3h2V7H8V5h3V2z" />
                                <path d="M6.5 20.5l1-7h9l1 7H6.5z" />
                                <path d="M5 22h14v-1.5l-1-1H6l-1 1V22z" />
                            </svg>
                        </div>

                        <span className="absolute -top-1 -right-1 flex h-5 w-5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-40" />
                            <span className="relative inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500">
                                <span className="text-white text-[10px] font-bold leading-none">!</span>
                            </span>
                        </span>
                    </div>
                </div>

                <p className="text-center text-gray-600 text-xs font-semibold tracking-[0.2em] uppercase mb-8">
                    {appConfig.appName}
                </p>

                <div className="bg-gray-900/70 border border-gray-800/80 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-sm">
                    <div className="h-[3px] bg-gradient-to-r from-red-700 via-red-500 to-orange-500" />

                    <div className="px-8 py-8">
                        <h1 className="text-2xl font-bold text-white text-center mb-3 tracking-tight">
                            {t('licenseInvalid.heading')}
                        </h1>

                        <p className="text-gray-400 text-sm text-center leading-relaxed mb-7">
                            {t('licenseInvalid.body')}
                        </p>

                        <div className="border-t border-gray-800 mb-7" />

                        <div className="bg-blue-950/30 border border-blue-900/40 rounded-xl p-5 mb-7">
                            <p className="text-gray-500 text-xs text-center mb-4 uppercase tracking-widest font-medium">
                                {t('licenseInvalid.restoreAccess')}
                            </p>

                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-9 h-9 rounded-full bg-blue-600/20 border border-blue-600/30 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    {appConfig.vendorUrl ? (
                                        <a
                                            href={appConfig.vendorUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-white font-semibold text-sm leading-tight hover:text-blue-400 transition-colors"
                                        >
                                            {appConfig.vendorName}
                                        </a>
                                    ) : (
                                        <p className="text-white font-semibold text-sm leading-tight">{appConfig.vendorName}</p>
                                    )}
                                    <p className="text-gray-500 text-xs">{t('licenseInvalid.softwareProvider')}</p>
                                </div>
                            </div>

                            <a
                                href={mailtoHref}
                                className="flex items-center gap-2.5 w-full px-3 py-2.5 bg-gray-800/60 hover:bg-gray-800 border border-gray-700/60 hover:border-blue-600/50 rounded-lg transition-all group"
                            >
                                <svg
                                    className="w-4 h-4 text-gray-500 group-hover:text-blue-400 flex-shrink-0 transition-colors"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                <span className="text-gray-300 group-hover:text-blue-400 text-sm font-medium transition-colors truncate">
                                    {appConfig.vendorEmail}
                                </span>
                            </a>
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="flex-1 py-2.5 px-4 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-xl text-sm font-medium transition-all border border-gray-700/60 hover:border-gray-600"
                            >
                                {t('licenseInvalid.tryAgain')}
                            </button>
                            <a
                                href={mailtoHref}
                                className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-medium text-center transition-all shadow-lg shadow-blue-900/30"
                            >
                                {t('licenseInvalid.contactSupport')}
                            </a>
                        </div>
                    </div>
                </div>

                <p className="text-center text-gray-700 text-xs mt-6">
                    {t('licenseInvalid.footer', { name: appConfig.appName })}
                </p>
            </div>
        </div>
    );
};

export default LicenseInvalidPage;
